import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { apiUrl } from './utils/api';
import { useLanguage } from '../i18n/LanguageContext';
import { formatDate } from './utils/date';

/** Cents → "$20.00". Display only; every amount arrives from the backend already in cents. */
export const centsToMoney = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);

/** One-line price for a plan, in the reader's language: "$20.00 / month · up to 150 tickets". */
export function planPriceLine(plan, t) {
  if (!plan) return '';
  const per = t(plan.billingInterval === 'week' ? 'bill.perWeek' : 'bill.perMonth');
  const parts = [`${centsToMoney(plan.basePriceCents)} ${per}`];
  if (plan.perTicketCents != null) {
    parts[0] = t('bill.usagePrice', { ticket: centsToMoney(plan.perTicketCents), min: centsToMoney(plan.basePriceCents), per });
  }
  if (plan.includedTickets != null && plan.perTicketCents == null) parts.push(t('bill.upToTickets', { n: plan.includedTickets }));
  if (plan.maxTrucks != null) parts.push(t('bill.upToTrucks', { n: plan.maxTrucks }));
  return parts.join(' · ');
}

const STATE_CHIP = {
  ACTIVE: { color: 'success', key: 'bill.stateActive' },
  TRIAL: { color: 'info', key: 'bill.stateTrial' },
  TRIAL_ENDED: { color: 'warning', key: 'bill.stateTrialEnded' },
  PAST_DUE: { color: 'warning', key: 'bill.statePastDue' },
  READ_ONLY: { color: 'error', key: 'bill.stateReadOnly' },
};

/**
 * Billing: where the account stands (trial days left, grace period, read-only) and the plans on offer.
 * Payment (Stripe Checkout / Apple Pay / Google Pay / ACH) plugs in here; until it is connected the
 * screen shows prices and says no card is needed yet.
 */
export default function Billing() {
  const { t, lang } = useLanguage();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await axios.get(`${apiUrl}/api/billing/status`);
        const p = await axios.get(`${apiUrl}/api/billing/plans`, { params: { businessType: s.data?.businessType || undefined } });
        if (!cancelled) {
          setStatus(s.data);
          setPlans(Array.isArray(p.data) ? p.data : []);
        }
      } catch (e) {
        if (!cancelled) setError(t('bill.loadFailed'));
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const planName = (p) => (lang === 'es' ? p.nameEs : p.nameEn);
  const chip = status && STATE_CHIP[status.state];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: 'auto' }}>
      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>{t('bill.title')}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!status && !error && <CircularProgress />}

      {status && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{t('bill.yourAccount')}</Typography>
            {chip && <Chip size="small" color={chip.color} label={t(chip.key)} />}
          </Stack>
          {status.state === 'TRIAL' && (
            <Typography sx={{ mt: 1, fontSize: 15 }}>
              {t('bill.trialLeft', { n: status.daysLeft ?? '—', date: formatDate(status.trialEndsAt) })}
            </Typography>
          )}
          {(status.state === 'TRIAL_ENDED' || status.state === 'PAST_DUE') && (
            <Typography sx={{ mt: 1, fontSize: 15 }}>{t('bill.graceLeft', { n: status.daysLeft ?? '—' })}</Typography>
          )}
          {status.state === 'READ_ONLY' && <Typography sx={{ mt: 1, fontSize: 15 }}>{t('bill.readOnlyHelp')}</Typography>}
          {status.plan && (
            <Typography sx={{ mt: 1, fontSize: 14, color: 'text.secondary' }}>
              {t('bill.plan')}: <strong>{planName(status.plan)}</strong> — {planPriceLine(status.plan, t)}
            </Typography>
          )}
        </Paper>
      )}

      {plans.length > 0 && (
        <>
          <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>{t('bill.plans')}</Typography>
          <Stack spacing={1.5}>
            {plans.map((p) => (
              <Paper key={p.code} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: status?.planCode === p.code ? 'primary.main' : undefined }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{planName(p)}</Typography>
                <Typography sx={{ fontSize: 15, mt: 0.5 }}>{planPriceLine(p, t)}</Typography>
              </Paper>
            ))}
          </Stack>
        </>
      )}

      {status && !status.paymentsEnabled && (
        <Alert severity="info" sx={{ mt: 2 }}>{t('bill.paymentsSoon')}</Alert>
      )}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>{t('bill.samplePrices')}</Typography>
    </Box>
  );
}
